/**
 * Basic search form component.
 * Handles input events, reset button toggling, and pop-panel focus behavior.
 */
class SearchForm extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (!this.input) return;

      // Listen for form reset
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));

      // Listen for input changes (debounced)
      this.input.addEventListener(
        "input",
        webvista.debounce((event) => {
          this.onChange(event);
        }, 300),
      );

      // Pop-panel mode: toggle container on focus
      if (this.hasAttribute("data-pop-panel")) {
        this.input.addEventListener("focusin", this.onFocusin.bind(this));
        this.addEventListener("focusout", this.onFocusout.bind(this));
      }
    }

    /**
     * Toggle reset button visibility based on input value
     */
    toggleResetButton() {
      const resetIsHidden = this.resetButton.classList.contains("hidden");
      if (this.input.value.length > 0 && resetIsHidden) {
        this.resetButton.classList.remove("hidden");
      } else if (this.input.value.length === 0 && !resetIsHidden) {
        this.resetButton.classList.add("hidden");
      }
    }

    onChange() {
      this.toggleResetButton();
    }

    /**
     * Handle form reset.
     * Prevents default to avoid restoring the URL-based search term.
     * @param {Event} event
     */
    onFormReset(event) {
      // Prevent default so the form reset doesn't set the value gotten from the url on page load
      event.preventDefault();

      this.input.value = "";
      this.input.focus();

      // Programmatic value changes don't fire 'input' events — dispatch manually
      this.input.dispatchEvent(
        new Event("input", {
          bubbles: true,
          cancelable: true,
        }),
      );
      this.toggleResetButton();
    }

    /**
     * Handle input focus-in: open the search container
     */
    onFocusin() {
      this.classList.add("in-focus");
      this.isOpen = true;
      this.input.setAttribute("aria-expanded", "true");

      // Activate header — makes transparent headers show their background
      this.toggleHeaderActive(true);
    }

    /**
     * Handle search blur: close the container if focus left entirely
     */
    onFocusout() {
      // Defer to get the latest active element (focus change has a delay)
      setTimeout(() => {
        if (this.contains(document.activeElement)) return;

        this.classList.remove("in-focus");
        this.isOpen = false;
        this.input.setAttribute("aria-expanded", "false");

        this.toggleHeaderActive(false);
      });
    }

    /**
     * Toggle header active state
     * @param {boolean} active
     */
    toggleHeaderActive(active = true) {
      this.header = this.header || this.closest(".section-header");
      if (this.header)
        this.header.classList.toggle("header--is-active", active);
    }
  }

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

/**
 * Main search component.
 * Extends SearchForm to synchronize input values across all search inputs on the page.
 */
class MainSearch extends SearchForm {
    connectedCallback() {
      super.connectedCallback();

      this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    }

    onFormReset(event) {
      super.onFormReset(event);

      this.keepInSync("", this.input);
    }

    /**
     * Handle input change and sync value to other search inputs
     * @param {Event} event
     */
    onChange(event) {
      super.onChange();

      this.keepInSync(event.target.value, this.input);
    }

    /**
     * Synchronize value across all search inputs
     * @param {string} value
     * @param {HTMLInputElement} from - The originating input to skip
     */
    keepInSync(value, from) {
      this.allSearchInputs.forEach((input) => {
        if (input !== from) input.value = value;
      });
    }
  }

if (!customElements.get("main-search")) {
  customElements.define("main-search", MainSearch);
}

/**
 * Predictive search component.
 * Extends SearchForm with live search results, caching, and multi-instance sync.
 */
class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.searchTerm = "";
      this.isOpen = false;
    }

    connectedCallback() {
      super.connectedCallback();

      // Results container
      this.searchResultsWrapper = this.querySelector(".search-results-wrapper");
      // All predictive-search instances on the page (for syncing cached results)
      this.allPredictiveSearchInstances =
        document.querySelectorAll("predictive-search");

      // Popular keyword click handlers
      this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
        suggestion.addEventListener(
          "click",
          this.onPopularKeywordClick.bind(this),
        );
      });

      this.abortController = new AbortController();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    /**
     * Get trimmed input value
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handle input change — show placeholder and fetch results
     */
    onChange() {
      super.onChange();

      const currentSearchTerm = this.getQuery();
      if (currentSearchTerm === this.searchTerm) return;
      this.searchTerm = currentSearchTerm;

      if (this.searchTerm.length <= 0) return this.closeResults(true);

      this.showPlaceholder();
      this.getSearchResults();
    }

    /**
     * Handle popular keyword click — fill input and trigger search
     */
    onPopularKeywordClick(event) {
      event.preventDefault();

      const keyword = event.currentTarget.getAttribute("data-keyword");
      if (keyword !== "") {
        this.input.value = keyword;

        this.input.focus();

        // Programmatic value changes don't fire 'input' events — dispatch manually
        this.input.dispatchEvent(
          new Event("input", {
            bubbles: true,
            cancelable: true,
          }),
        );
      }
    }

    onFormReset(event) {
      super.onFormReset(event);

      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }

    /**
     * Handle focus-in — restore or refresh search results
     */
    onFocusin() {
      super.onFocusin();

      // Re-read the query — it may have changed from another search input
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm && currentSearchTerm === this.searchTerm) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        // Cached results exist — just re-show
        this.showResults();
      } else {
        // No cached results — re-fetch
        this.getSearchResults();
      }
    }

    /**
     * Show loading placeholder while fetching results
     */
    showPlaceholder() {
      this.placeholder =
        this.placeholder ||
        this.querySelector(`#Placeholder-Search-Form-${this.dataset.section}`)
          .innerHTML;
      this.searchResultsWrapper.innerHTML = this.placeholder;
    }

    /**
     * Fetch search results (uses cache when available)
     */
    getSearchResults() {
      const queryKey = this.searchTerm.replace(/\s+/g, "-").toLowerCase();
      this.setLiveRegionLoading();

      // Use cached results if available
      if (this.cachedResults[queryKey])
        return this.renderSearchResults(this.cachedResults[queryKey]);

      // Fetch fresh results from the predictive search API
      const url = new URL(
        window.routes.predictive_search_url,
        window.location.origin,
      );

      url.search = new URLSearchParams({
        q: this.searchTerm,
        "resources[limit]": 10,
        "resources[limit_scope]": "each",
        // PJ CUSTOM: Shopify's predictive search defaults to
        // title,product_type,variants.title,vendor — tags are NOT searched,
        // which hides every Artist_/Rarity_/HP_/Set_/Printing_ tag on our cards.
        // Measured: "komiya" 1 -> 10 results, "HP_60" 0 -> 11, "charizard" unchanged.
        // Re-apply after any Ascent theme update (playbook §5-D).
        "resources[options][fields]":
          "title,product_type,variants.title,vendor,tag",
        section_id: "predictive-search",
      });

      webvista
        .fetchHtml(url.toString(), this.abortController.signal)
        .then((html) => {
          const shopifySection = html.querySelector(".shopify-section");
          let resultsMarkup = shopifySection ? shopifySection.innerHTML : "";
          if (!resultsMarkup) throw new Error("No search results found");

          // Prevent duplicate IDs across multiple search instances
          resultsMarkup = this.preventDuplicatedIDs(resultsMarkup);

          // Sync cached results across all predictive-search instances
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            },
          );

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }

          this.removeAttribute("loading");
          this.searchResultsWrapper.innerHTML = " ";
          this.removeAttribute("results");
        });
    }

    /**
     * Render search results HTML and re-initialize lazy images / tooltips
     * @param {string} resultsHtml
     */
    renderSearchResults(resultsHtml) {
      this.searchResultsWrapper.innerHTML = resultsHtml;
      this.setAttribute("results", "true");

      this.setLiveRegionResults();
      this.showResults();

      webvista.initLazyImages();
      webvista.initTooltips();
    }

    /**
     * Calculate the max height for the results container
     * @returns {number}
     */
    getResultsMaxHeight() {
      this.resultsMaxHeight =
        window.innerHeight -
        this.querySelector(".predictive-search-main").getBoundingClientRect()
          .top -
        32;

      return this.resultsMaxHeight;
    }

    /**
     * Show predictive search results.
     * The 'open' attribute controls visibility.
     */
    showResults() {
      this.style.setProperty(
        "--result-max-height",
        `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`,
      );
      this.setAttribute("open", "true");
    }

    /**
     * Close predictive search results.
     * If clearSearchTerm is true, also clears the input and cached markup.
     * The 'open' attribute controls container visibility; 'results' tracks data presence.
     * @param {boolean} clearSearchTerm
     */
    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = "";
        this.searchResultsWrapper.innerHTML = " "; // leave a space to avoid display none
        this.removeAttribute("results");
      }

      this.removeAttribute("loading");
      this.removeAttribute("open");

      this.resultsMaxHeight = false;
      this.style.removeProperty("--result-max-height");
    }

    /**
     * Prevent duplicate IDs when multiple search instances exist on the page
     * @param {string} html
     * @returns {string}
     */
    preventDuplicatedIDs(html) {
      if (!this.id) return html;

      return html.replace(
        /(id|aria-controls|aria-labelledby)="([^"]+)"/g,
        (match, attr, value) => {
          return `${attr}="${value}_${this.id}"`;
        },
      );
    }

    /**
     * Set loading state for the live region (assistive devices)
     */
    setLiveRegionLoading() {
      this.statusElement =
        this.statusElement ||
        this.querySelector(".predictive-search-status");
      this.loadingText =
        this.loadingText || this.getAttribute("data-loading-text");
      this.setAttribute("loading", "true");
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Update live region with search result count (assistive devices)
     */
    setLiveRegionResults() {
      this.removeAttribute("loading");
      this.setLiveRegionText(
        this.querySelector("[data-predictive-search-live-region-count-value]")
          .textContent,
      );
    }

    /**
     * Update live region text for assistive devices.
     * Uses role="status" (implicit aria-live="polite") — screen readers
     * automatically announce content changes without aria-hidden toggling.
     * @param {string} statusText
     */
    setLiveRegionText(statusText) {
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.textContent = "";
      }, 1000);
    }
  }

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
